import React from 'react';
import './IQCLanding.scss';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { COMPETITION_IQC, IQC_COMPETITION_ID, SIGN_UP_IQC } from '../../shared/constants';
import { toggleGuidelinesModal } from '../../actions/CompetitionsActions';
import { useDispatch, useSelector } from 'react-redux';
import { getIsLoggedInSelector, hasConsultantOrResearcherPermission } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { injectIntl, IntlShape } from 'react-intl';
import { LanguageSelector } from './LanguageSelector';
import analytics from '../../shared/services/analytics/analytics';
import { IQCImageCarousel } from './IQCImageCarousel';
import { SharingButtons } from './SharingButtons';
import { addModifiers } from '../../shared/helpers';
import { IQCTitle } from './IQCTitle';
import { AlphathonRegister } from '../AlphathonLanding/AlphathonRegister';
import { Countdown } from './Countdown';
import moment from 'moment';
import { IQC_REGISTRATION_OPEN_DATE } from './constants';
import { formatLocaleDate, SUPPORTED_BROWSER_LANGUAGE_CODES, useI18nContext } from '../../shared/services/i18n';
import { useInterval } from 'react-use';

export const IQCLandingWithoutIntl = ({ intl }: { intl: IntlShape }) => {
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const history = useHistory();
  const dispatch = useDispatch();

  const isPrivelegedUser = useSelector(hasConsultantOrResearcherPermission);

  const getIsRegistrationOpened = () => moment().isSameOrAfter(moment(IQC_REGISTRATION_OPEN_DATE));
  const [isRegistrationOpened, setIsRegistrationOpened] = React.useState(getIsRegistrationOpened);
  useInterval(() => setIsRegistrationOpened(getIsRegistrationOpened()), !isRegistrationOpened ? 1000 : null);

  const { language } = useI18nContext();

  const onJoinClick = (event_label: string) => () => {
    if (isLoggedIn) {
      dispatch(toggleGuidelinesModal(true));
      history.push(COMPETITION_IQC);
    } else {
      history.push(SIGN_UP_IQC);
    }

    analytics.trackCustomEvent('click_iqc_page', { event_label });
  };

  const year = moment().format('YYYY');

  const isSeminarScheduleReady = true;

  return (
    <div className={addModifiers('iqc', [language, isPrivelegedUser ? 'priveleged' : ''])}>
      <Helmet>
        <title>International Quant Championship 2026</title>
        <meta property="og:title" content="International Quant Championship 2026" />
        <meta
          property="og:description"
          content="Join the International Quant Championship and Compete for a Chance To Win Cash Prizes from a Pool of up to $100,000."
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        {/*@ts-ignore*/}
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin />
        <link
          href="https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,wght@0,200;0,300;0,400;0,700;1,300&family=Chakra+Petch:ital,wght@0,400;0,500;1,400&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <div className="iqc__section">
        <div className="iqc__section-container">
          {/*<div className="iqc__sphere"></div>*/}
          <div className={addModifiers('iqc__outer', [isLoggedIn ? 'logged-in' : 'logged-out'])}>
            <div className="iqc__language-selector-wrapper iqc__language-selector-wrapper--desktop">
              <LanguageSelector />
            </div>

            <div className="iqc__introduction">
              <div className="iqc__iqc-logo"></div>
              <IQCTitle language={language} />
              <p className="iqc__subtitle">
                <FormattedMessageHelper id="iqc.subtitle2" />
              </p>

              <div className="iqc__sharing-buttons">
                <FormattedMessageHelper tagName="p" id="iqc.shareOn" />
                <SharingButtons />
                {language === SUPPORTED_BROWSER_LANGUAGE_CODES.chineseSimplified && (
                  <FormattedMessageHelper tagName="p" id="iqc.shareOn.suffix" />
                )}
              </div>
            </div>

            <div className="iqc__registration">
              {!isRegistrationOpened && (
                <>
                  <div className="iqc__countdown-container">
                    <FormattedMessageHelper
                      tagName="p"
                      id="iqc.registrationWillOpen"
                      values={{ formattedStartDate: formatLocaleDate(language, moment(IQC_REGISTRATION_OPEN_DATE)) }}
                    />
                    <Countdown />
                  </div>
                </>
              )}

              {isRegistrationOpened ? (
                isLoggedIn ? (
                  <div className="iqc__open-competition">
                    <button onClick={onJoinClick('open_competition')} className="iqc__join-button iqc__join-button--xl">
                      <FormattedMessageHelper id="iqc.openCompetitionButton" />
                    </button>
                  </div>
                ) : (
                  <AlphathonRegister competitionId={IQC_COMPETITION_ID} />
                )
              ) : null}

              <div className="iqc__sharing-buttons">
                <FormattedMessageHelper tagName="p" id="iqc.shareOn" />
                <SharingButtons />
                {language === SUPPORTED_BROWSER_LANGUAGE_CODES.chineseSimplified && (
                  <FormattedMessageHelper tagName="p" id="iqc.shareOn.suffix" />
                )}
              </div>
            </div>
          </div>

          <div className="iqc__section-divider"></div>
        </div>
      </div>
      <div className="iqc__section-container">
        <div className="iqc__sphere"></div>
        <div className="iqc__inner">
          <div className="iqc__cash">
            <h2 className={addModifiers('iqc__title', ['h2', 'chakra-petch', 'gradient'])}>
              <FormattedMessageHelper id="iqc.opportunityTitle" />
            </h2>
            <p className="iqc__cash-text">
              <FormattedMessageHelper id="iqc.opportunityText" />
            </p>
            <div className="iqc__opportunity-grid">
              <div className="iqc__opportunity-card">
                <FormattedMessageHelper
                  id="iqc.opportunityListItem1"
                  values={{
                    highlight: (children: string) => (
                      <span className="iqc__text-gradient">
                        <b>{children}</b>
                      </span>
                    ),
                  }}
                />
              </div>
              <div className="iqc__opportunity-card">
                <FormattedMessageHelper id="iqc.opportunityListItem2" />
              </div>
              <div className="iqc__opportunity-card">
                <FormattedMessageHelper id="iqc.opportunityListItem3" />
              </div>
              <div className="iqc__opportunity-card">
                <FormattedMessageHelper id="iqc.opportunityListItem4" />
              </div>
            </div>
            <p className="iqc__cash-text iqc__cash-text--s">
              <FormattedMessageHelper id="iqc.opportunityExtra" values={{ asterisk: <i className="iqc__" /> }} />
            </p>
          </div>

          {(isSeminarScheduleReady || isRegistrationOpened) && (
            <div className="iqc__resources">
              <h2 className={addModifiers('iqc__title', ['h2', 'chakra-petch', 'gradient'])}>
                <FormattedMessageHelper id="iqc.resourcesTitle" />
              </h2>
              <div className="iqc__resources-content">
                {isSeminarScheduleReady && (
                  <p className={addModifiers('iqc__resources-text', [!isRegistrationOpened ? 'full-width' : ''])}>
                    <FormattedMessageHelper id="iqc.resourcesText1" />
                  </p>
                )}

                {/* Because the IQC Landing on worldquant.com is not available until IQC registration open */}
                {isRegistrationOpened && (
                  <p className="iqc__resources-text">
                    <FormattedMessageHelper id="iqc.resourcesText2" />
                  </p>
                )}
              </div>
              {!isLoggedIn && isRegistrationOpened && (
                <button onClick={onJoinClick('sign_up_for_iqc')} className="iqc__join-button iqc__join-button--mobile-only">
                  <FormattedMessageHelper id={'iqc.signUpForIQCButton'} />
                </button>
              )}
            </div>
          )}

          <div>
            <h2 className={addModifiers('iqc__title', ['h2', 'chakra-petch', 'gradient'])}>
              <FormattedMessageHelper id="iqc.lastIQCHighlightsTitle" />
            </h2>
            <IQCImageCarousel />
          </div>
          <div className="iqc__section-divider"></div>

          <div className="iqc__language-selector-wrapper iqc__language-selector-wrapper--mobile">
            <LanguageSelector />
          </div>
          <div className="iqc__section-divider"></div>

          <footer className="iqc__footer">
            <div className="iqc__copyright"></div>
            <div className="iqc__copyright-text">
              <FormattedMessageHelper id="iqc.copyrightText" values={{ year }} />
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default injectIntl(IQCLandingWithoutIntl);
